import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import Conversation from '../../Pages/Conversation/conversation';
import apiService from '../../Services/apiServices';
import { getFormattedTimestamp, handledButtonsUI } from '../Methods/Methods';

const WorkflowChat = forwardRef(({
  tabId,
  sessionId: subSessionId,
  token: subToken,
  messages: tabMessages,
  code: tabCode,
  loading: tabLoading,
  error: tabError,
  onUpdateSession,
  showMaximumChat,
  setIsShowMaximumChat,
  flow,
  isActive,
  addMessagesFeedback,
}, ref) => {
  const [inputValue, setInputValue] = useState('');
  const [disableInputBox, setDisableInputBox] = useState(false);
  const [showMsgs, setShowMsgs] = useState(true);
  const [isFeedbackPop, setIsFeedbackPop] = useState(false);
  const [checkThumsDownFeedback, setCheckThumsDownFeedback] = useState([]);
  const [indexForPopUp, setIndexForPopUp] = useState(0);
  const [isSubscriber, setisSubscriber] = useState(true);
  const [showR1FeedbackPop, setShowR1FeedbackPop] = useState({ isActive: false, option: '' });
  const [saveFeedbackData, setSaveFeedbackData] = useState('');
  const [showUrlPopUp, setShowUrlPopUp] = useState({ isActive: false, url: '' });
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  const [count, setCount] = useState(0);

  const scrollToBottom = () => {
    const el = messagesEndRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  };

  useEffect(() => {
    setTimeout(scrollToBottom, 350);
  }, [tabMessages]);

  // Re-enable last message's buttons/inputs when tab becomes visible or loading finishes
  useEffect(() => {
    if (!containerRef.current || tabLoading) return;
    const container = containerRef.current;
    
    // First, disable ALL interactive elements in this tab
    handledButtonsUI(container);

    // Then re-enable only the last conversation's buttons/inputs
    const conversations = container.getElementsByClassName('oneConversation');
    if (conversations.length === 0) return;
    const lastConversation = conversations[conversations.length - 1];
    
    const actionTabs = lastConversation.getElementsByClassName('action-tab');
    for (let i = 0; i < actionTabs.length; i++) {
      actionTabs[i].disabled = false;
      actionTabs[i].style.cursor = 'pointer';
      actionTabs[i].style.pointerEvents = 'auto';
    }
    const optionButtons = lastConversation.getElementsByClassName('option-button');
    for (let i = 0; i < optionButtons.length; i++) {
      optionButtons[i].disabled = false;
      optionButtons[i].style.cursor = 'pointer';
      optionButtons[i].style.pointerEvents = 'auto';
    }
    const formControls = lastConversation.getElementsByClassName('form-control');
    for (let i = 0; i < formControls.length; i++) {
      formControls[i].disabled = false;
      formControls[i].style.cursor = 'auto';
    }
    const a1Wraps = lastConversation.getElementsByClassName('a1-wrap');
    for (let i = 0; i < a1Wraps.length; i++) {
      a1Wraps[i].style.cursor = 'pointer';
    }
  }, [tabMessages, tabLoading]);

  const addMessagesFeedbackLocal = async (msg, node, label, input, inputText, body) => {
    try {
      handledButtonsUI(containerRef.current);
      const timestamp = getFormattedTimestamp();
      setCount((c) => c + 1);

      let updatedMessages = [...tabMessages];

      if (msg || (label && inputText !== 'loader')) {
        updatedMessages.push({
          text: inputText || msg || label,
          type: 'CUSTOMER',
          date: new Date().toISOString(),
          messageCount: `CUSTOMER-${count + 1}`,
          time: timestamp,
        });
      }

      if (inputText !== 'loader') {
        updatedMessages.push({
          text: '...',
          type: 'SERVER',
          date: new Date().toISOString(),
          messageCount: `SERVER-${count + 1}`,
          isLoading: true,
          time: timestamp,
        });
      }

      onUpdateSession(tabId, { messages: updatedMessages, loading: true });

      const params = {
        bot: 'agent-assist',
        code: node === null ? null : node || tabCode,
        token: subToken,
        label: encodeURIComponent(JSON.stringify({ key1: label, count: Math.random() })),
      };

      let serverResponse = await apiService.inputResonsePostPacks(params, body);

      const data = serverResponse?.data?.chatResponse;
      if (!data || data?.entries?.length === 0) {
        const errorMsg = {
          text: 'Failed to get a response from the server. Please try again later.',
          type: 'SERVER',
          date: new Date().toISOString(),
          messageCount: `SERVER-${count + 1}`,
          isLoading: false,
          isFailed: true,
          time: timestamp,
        };
        const finalMessages = updatedMessages.map((m) => (m.isLoading ? errorMsg : m));
        onUpdateSession(tabId, { messages: finalMessages, loading: false, error: true });
        return;
      }

      setDisableInputBox(!data?.enableDefaultTextBox);
      const newCode = data?.entries?.[data.entries.length - 1]?.code || tabCode;
      let serverData = {
        entries: data.entries,
        type: 'SERVER',
        messageCount: `SERVER-${count + 1}`,
        time: timestamp,
        viewMenuInfo: data?.viewMenuInfo || null,
        end: data?.end || false,
      };

      let finalMessages;
      if (inputText === 'loader') {
        finalMessages = [...updatedMessages];
        finalMessages[finalMessages.length - 1] = serverData;
      } else {
        finalMessages = updatedMessages.map((m) => (m.isLoading ? serverData : m));
      }
      onUpdateSession(tabId, { messages: finalMessages, code: newCode, loading: false, error: false });
    } catch (error) {
      console.error('WorkflowChat feedback error:', error);
      const timestamp = getFormattedTimestamp();
      const errorMsg = {
        text: 'Failed to get a response from the server. Please try again later.',
        type: 'SERVER',
        date: new Date().toISOString(),
        messageCount: `SERVER-${count + 1}`,
        isLoading: false,
        isFailed: true,
        time: timestamp,
      };
      const finalMessages = tabMessages.map((m) => (m.isLoading ? errorMsg : m));
      onUpdateSession(tabId, { messages: finalMessages, loading: false, error: true });
    }
  };

  const addMessages = async (msg, node, label, displayText, checkUpdate, tag) => {
    try {
      handledButtonsUI(containerRef.current);
      const timestamp = getFormattedTimestamp();
      setCount((c) => c + 1);

      let updatedMessages = [...tabMessages];

      if (msg || (label && displayText !== 'loader')) {
        updatedMessages.push({
          text: displayText || msg || label,
          type: 'CUSTOMER',
          date: new Date().toISOString(),
          messageCount: `CUSTOMER-${count + 1}`,
          time: timestamp,
        });
      }

      if (displayText !== 'loader') {
        updatedMessages.push({
          text: '...',
          type: 'SERVER',
          date: new Date().toISOString(),
          messageCount: `SERVER-${count + 1}`,
          isLoading: true,
          time: timestamp,
        });
      }

      onUpdateSession(tabId, { messages: updatedMessages, loading: true });

      const params = {
        bot: 'agent-assist',
        ...(!checkUpdate && node !== 'Blank / Transfer / Call Drop' && { code: node === null ? null : node || tabCode }),
        token: subToken,
        ...(msg && {
          input: encodeURIComponent(JSON.stringify({ key1: msg, count: Math.random() })),
        }),
        label: encodeURIComponent(JSON.stringify({ key1: label, count: Math.random() })),
        ...(tag === 'MenuButton' && { currentNode: tabCode }),
      };

      let serverResponse = await apiService.inputResonse(params);

      if (!serverResponse?.data?.chatResponse || serverResponse?.data?.chatResponse?.entries?.length === 0) {
        const errorMsg = {
          text: 'Failed to get a response from the server. Please try again later.',
          type: 'SERVER',
          date: new Date().toISOString(),
          messageCount: `SERVER-${count + 1}`,
          isLoading: false,
          isFailed: true,
          time: timestamp,
        };
        const finalMessages = updatedMessages.map((m) => (m.isLoading ? errorMsg : m));
        onUpdateSession(tabId, { messages: finalMessages, loading: false, error: true });
        return;
      }

      const data = serverResponse.data.chatResponse;
      setDisableInputBox(!data?.enableDefaultTextBox);

      const newCode = data?.entries?.[data.entries.length - 1]?.code || tabCode;
      let serverData = {
        entries: data.entries,
        type: 'SERVER',
        messageCount: `SERVER-${count + 1}`,
        time: timestamp,
        viewMenuInfo: data?.viewMenuInfo || null,
        end: data?.end || false,
      };

      let finalMessages;
      if (displayText === 'loader') {
        // Replace the last message (A62 loader entry) with new response
        finalMessages = [...updatedMessages];
        finalMessages[finalMessages.length - 1] = serverData;
      } else {
        finalMessages = updatedMessages.map((m) => (m.isLoading ? serverData : m));
      }
      onUpdateSession(tabId, { messages: finalMessages, code: newCode, loading: false, error: false });
    } catch (error) {
      console.error('WorkflowChat error:', error);
      const timestamp = getFormattedTimestamp();
      const errorMsg = {
        text: 'Failed to get a response from the server. Please try again later.',
        type: 'SERVER',
        date: new Date().toISOString(),
        messageCount: `SERVER-${count + 1}`,
        isLoading: false,
        isFailed: true,
        time: timestamp,
      };
      const finalMessages = tabMessages.map((m) => (m.isLoading ? errorMsg : m));
      onUpdateSession(tabId, { messages: finalMessages, loading: false, error: true });
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      addMessages(inputValue);
      setInputValue('');
    }
  };

  const handleSend = () => {
    if (inputValue.trim()) {
      addMessages(inputValue);
      setInputValue('');
    }
  };

  const handleInput = (e) => setInputValue(e.target.value);

  useImperativeHandle(ref, () => ({
    addMessages,
    getDisableInputBox: () => disableInputBox,
    getLoading: () => tabLoading,
  }));

  if (tabError && tabMessages.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#d02f92' }}>
        Failed to initialize workflow. Please try again.
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ display: isActive ? 'flex' : 'none', flexDirection: 'column', height: '100%', width: '100%' }}>
      <div
        ref={messagesEndRef}
        style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}
        className={showMaximumChat ? 'fullPageBotChatWrap' : 'bot-chat-wrap'}
      >
        <Conversation
          key={tabId}
          setShowMsgs={setShowMsgs}
          showMsgs={showMsgs}
          messages={tabMessages}
          updateMessage={() => {}}
          setIsFeedbackPop={setIsFeedbackPop}
          isFeedbackPop={isFeedbackPop}
          clearMessages={() => {}}
          loading={tabLoading}
          showMaximumChat={showMaximumChat}
          scrollToBottom={scrollToBottom}
          token={subToken}
          sessionId={subSessionId}
          startLogin={() => {}}
          setIsThirtyMinPopUP={() => {}}
          IsThirtyMinPopUP={false}
          setIsShowBot={() => {}}
          setDisableInputBox={setDisableInputBox}
          checkThumsDownFeedback={checkThumsDownFeedback}
          setCheckThumsDownFeedback={setCheckThumsDownFeedback}
          setIndexForPopUp={setIndexForPopUp}
          setisSubscriber={setisSubscriber}
          isSubscriber={isSubscriber}
          addMessages={addMessages}
          addMessagesPhase2={() => {}}
          setIsShowMaximumChat={setIsShowMaximumChat}
          saveFeedbackData={saveFeedbackData}
          setSaveFeedbackData={setSaveFeedbackData}
          showR1FeedbackPop={showR1FeedbackPop}
          setShowR1FeedbackPop={setShowR1FeedbackPop}
          setShowUrlPopUp={setShowUrlPopUp}
          flow={flow}
          downtime={{ isDowntime: false, message: '' }}
          isWorkflowTab={true}
          addMessagesFeedback={addMessagesFeedbackLocal}
        />
      </div>
    </div>
  );
});

export default WorkflowChat;
